import React from 'react';
import classnames from 'classnames';
import {history} from 'client/history';

import Checkbox from '../../../atoms/Checkbox/Checkbox.react';
import './data-table.scss';

import DataTableCell from './DataTableCell';
import {useDataTableContext} from './DataTableContext';
import type {DataTableRowProps} from './DataTable.types';

/**
 * Renders a single `<tr>` for DataTable.
 *
 * Supports:
 * - Row selection (checkbox in first cell)
 * - Click-to-navigate (via `rowLinkFunc`)
 * - Generic row click handler (via `onRowClick`)
 * - Selected-row highlighting
 *
 */
function DataTableRow<TData>({row}: DataTableRowProps<TData>) {
  const {onRowClick, rowLinkFunc, enableRowSelection, disableRowHover} =
    useDataTableContext<TData>();
  const isSelected = row.getIsSelected();
  const href = rowLinkFunc ? rowLinkFunc(row.original) : undefined;
  const isLink = !!href || !!onRowClick;

  const handleClick = (_e: React.MouseEvent<HTMLTableRowElement>) => {
    if (onRowClick) {
      onRowClick(row.original);
    } else if (href) {
      history.push(href);
    }
  };

  return (
    <tr
      className={classnames('data-table__row', {
        'data-table__row--selected': isSelected,
        'data-table__row--link': isLink,
        'data-table__row--no-hover': disableRowHover
      })}
      data-testid={`data-table-row-${row.index}`}
      {...(href ? {href, role: 'link'} : {})}
      {...(isLink ? {onClick: handleClick} : {})}
    >
      {enableRowSelection && (
        <td
          className='data-table__cell data-table__selection-cell'
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            label={`Select row ${row.index + 1}`}
            className='data-table__row-select'
            checked={isSelected}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              row.toggleSelected();
            }}
            size='s'
          />
        </td>
      )}

      {row.getVisibleCells().map((cell) => (
        <DataTableCell key={cell.id} cell={cell} />
      ))}
    </tr>
  );
}

export default DataTableRow;
